// Versioned save (docs/08 §8): localStorage main slot, IndexedDB rolling backups, lz-string export.
import { compressToBase64, decompressFromBase64 } from 'lz-string';
import { platform } from '../platform/platform';
import { Decimal } from '../sim/decimal';
import { createInitialState, defaultHand, defaultKeys, defaultSettings, SAVE_VERSION } from '../sim/meta';
import type { GameState } from '../sim/types';

export const SAVE_KEY = 'maw.save.v1';
const DEC_TAG = '$d';

export interface SaveEnvelope {
  version: number;
  savedAt: number;
  checksum: string;
  data: unknown;
}

// ── Serialization (Decimals become {"$d": "1e30"}) ────────────────────

export function toPlain(state: GameState): unknown {
  return JSON.parse(
    JSON.stringify(state, function (this: unknown, key: string, value: unknown) {
      const raw = (this as Record<string, unknown>)[key];
      if (raw instanceof Decimal) return { [DEC_TAG]: raw.toString() };
      return value;
    }),
  );
}

export function fromPlain(data: unknown): GameState {
  const revive = (v: any): any => {
    if (v === null || typeof v !== 'object') return v;
    if (Array.isArray(v)) return v.map(revive);
    if (typeof v[DEC_TAG] === 'string' && Object.keys(v).length === 1) return new Decimal(v[DEC_TAG]);
    const out: Record<string, unknown> = {};
    for (const [k, x] of Object.entries(v)) out[k] = revive(x);
    return out;
  };
  return revive(data) as GameState;
}

/** FNV-1a over the JSON text; detects accidental corruption, not tampering. */
export function checksum(text: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}

// ── Migrations ────────────────────────────────────────────────────────

/** migrations[v] upgrades data from version v to v + 1. */
export const migrations: Record<number, (d: any) => any> = {
  // v6: the Hand can decline gamble offers.
  5: (d) => {
    if (d.meta?.hand) d.meta.hand.declineGambles ??= false;
    return d;
  },
  // v5 (Phase 5): achievement progress counters.
  4: (d) => {
    if (d.meta?.lifetime) d.meta.lifetime.counters ??= {};
    if (d.meta?.settings) {
      d.meta.settings.keys = { ...defaultKeys(), ...d.meta.settings.keys };
      d.meta.settings.repeatMs ??= 700;
    }
    return d;
  },
  // v4 (Phase 4): Devour and Miracle counts, the payphone in the intermission.
  3: (d) => {
    const l = d.meta?.lifetime;
    if (l) {
      l.devours ??= 0;
      l.miracles ??= 0;
    }
    if (d.run?.intermission) d.run.intermission.phone ??= null;
    return d;
  },
  // v3 (Phase 3): Reckonings, hexes, deals, bonuses, Molt and relics.
  2: (d) => {
    const m = d.meta;
    if (m) {
      m.teeth ??= 0;
      m.molts ??= 0;
      m.form ??= 'brass';
      m.cycleMaxTribute ??= m.lifetime?.maxTribute ?? 0;
      m.relics ??= { owned: [], slotted: [null, null, null], shards: 0 };
      m.almanac = { symbols: [], charms: [], relics: [], collectors: [], ...m.almanac };
      m.history ??= [];
      m.achievements ??= [];
      m.runSize ??= null;
    }
    const r = d.run;
    if (r) {
      r.reck ??= { seen: [], upcoming: {}, beaten: 0 };
      r.hexes ??= [];
      r.doom ??= 0;
      r.deals ??= [];
      r.nightShift ??= null;
      r.gamble ??= null;
      r.flags ??= {};
      r.stats = { destroyed: 0, bonuses: 0, ...r.stats };
      if (r.intermission) {
        r.intermission.deal ??= null;
        r.intermission.preview ??= [];
        r.intermission.flags ??= {};
      }
    }
    return d;
  },
  // v2 (Phase 3): hunger moods, machine meals and per-reel hold costs on the run.
  1: (d) => {
    if (d.run) {
      d.run.mood ??= { temp: null, left: 0, calmStreak: 0, adoringNext: false };
      d.run.eaten ??= [];
      d.run.holdPaid ??= (d.run.held ?? []).map((h: boolean) => (h ? 1 : 0));
    }
    return d;
  },
};

export function migrate(data: any, fromVersion: number): any {
  let d = data;
  for (let v = fromVersion; v < SAVE_VERSION; v++) {
    const m = migrations[v];
    if (!m) throw new Error(`no migration from save version ${v}`);
    d = m(d);
    d.version = v + 1;
  }
  return d;
}

/** Fill fields added since the save was written (non-breaking additions). */
function normalize(state: GameState): GameState {
  const fresh = createInitialState(state.meta?.seed ?? 'recovered');
  state.meta = { ...fresh.meta, ...state.meta, settings: { ...defaultSettings(), ...state.meta?.settings } };
  state.meta.settings.keys = { ...defaultKeys(), ...state.meta.settings.keys };
  state.meta.lifetime = { ...fresh.meta.lifetime, ...state.meta.lifetime };
  state.meta.hand = { ...defaultHand(), ...state.meta.hand };
  state.meta.almanac = { ...fresh.meta.almanac, ...state.meta.almanac };
  state.version = SAVE_VERSION;
  return state;
}

export function serialize(state: GameState, now: number): string {
  const data = toPlain(state);
  const text = JSON.stringify(data);
  const env: SaveEnvelope = { version: SAVE_VERSION, savedAt: now, checksum: checksum(text), data };
  return JSON.stringify(env);
}

export function deserialize(text: string): GameState {
  const env = JSON.parse(text) as SaveEnvelope;
  if (!env || typeof env !== 'object' || !('data' in env)) throw new Error('not a MAW save');
  if (checksum(JSON.stringify(env.data)) !== env.checksum) throw new Error('save checksum mismatch');
  if (env.version > SAVE_VERSION) throw new Error('save is from a newer version');
  const data = env.version < SAVE_VERSION ? migrate(env.data, env.version) : env.data;
  return normalize(fromPlain(data));
}

export function exportString(state: GameState, now: number): string {
  return compressToBase64(serialize(state, now));
}

export function importString(s: string): GameState {
  const text = decompressFromBase64(s.trim());
  if (!text) throw new Error('could not decode save string');
  return deserialize(text);
}

// ── Storage (browser only) ────────────────────────────────────────────

export function loadLocal(): GameState | null {
  try {
    const text = localStorage.getItem(SAVE_KEY);
    return text ? deserialize(text) : null;
  } catch (e) {
    console.error('failed to load save', e);
    return null;
  }
}

export function saveLocal(state: GameState): string {
  const text = serialize(state, Date.now());
  try {
    localStorage.setItem(SAVE_KEY, text);
  } catch (e) {
    console.error('failed to save', e);
  }
  // Desktop: mirror to userData/save.json for Steam Auto-Cloud (docs/08 §save).
  platform.saveMirror(text);
  return text;
}

const BACKUP_KEY = 'maw.backups';
const BACKUP_COUNT = 5;

export async function pushBackup(text: string): Promise<void> {
  try {
    const { get, set } = await import('idb-keyval');
    const list = ((await get(BACKUP_KEY)) as { savedAt: number; text: string }[] | undefined) ?? [];
    list.unshift({ savedAt: Date.now(), text });
    await set(BACKUP_KEY, list.slice(0, BACKUP_COUNT));
  } catch (e) {
    console.warn('backup failed', e);
  }
}

export async function listBackups(): Promise<{ savedAt: number; text: string }[]> {
  try {
    const { get } = await import('idb-keyval');
    return ((await get(BACKUP_KEY)) as { savedAt: number; text: string }[] | undefined) ?? [];
  } catch {
    return [];
  }
}

export function clearLocal(): void {
  localStorage.removeItem(SAVE_KEY);
}
